// FOS-SAMPLE-TRAVEL-001 (Rob, 2026-09-28): after a bottle is added, ask whether it is a full
// bottle, a travel size or a sample; for travel and samples ask the size (1.5 ml is typical)
// and how many, so the shelf shows "Sample · 1.5 ml ×3" and the total ml, and lowering the
// count tracks vials given away. The kind is stored per physical bottle
// (fragrance.bottle_inventory.bottle_format) so one fragrance can hold a full bottle AND samples.

import { loadOrigin, saveOrigin } from './bottleOrigin.js'
import { publishBottlesChanged } from './collectionEvents.js'

export const BOTTLE_FORMATS = [
  ['full', 'Full bottle'],
  ['travel', 'Travel'],
  ['sample', 'Sample'],
]

export const FORMAT_LABELS = Object.fromEntries(BOTTLE_FORMATS)

// Tap-to-pick sizes. Samples are usually 1.5 ml; travel sprays usually 10 ml.
export const FORMAT_SIZES = {
  sample: [0.7, 1, 1.5, 2, 3, 5],
  travel: [5, 8, 10, 15, 20, 30],
}
export const FORMAT_DEFAULT_SIZE = { sample: 1.5, travel: 10 }
export const MAX_ON_HAND = 100
export const ON_HAND_STATUSES = new Set(['sealed', 'in_use'])

export function isCountedFormat(format) {
  return format === 'sample' || format === 'travel'
}

// One decimal place, above 0 and at most 1000 ml. Returns a number or null for blank.
// Throws a plain-English error for anything else.
export function parseBottleSize(value) {
  if (value === '' || value == null) return null
  const text = String(value).trim().replace(',', '.')
  if (!/^\d{1,4}(\.\d)?$/.test(text)) throw new Error('Enter the size in ml, like 1.5 or 10.')
  const ml = Number(text)
  if (!(ml > 0) || ml > 1000) throw new Error('Bottle size must be more than 0 ml and at most 1000 ml.')
  return ml
}

// Keeps what a person types in a size box to digits and one decimal place.
export function cleanSizeInput(value) {
  const text = String(value ?? '').replace(',', '.').replace(/[^0-9.]/g, '')
  const [whole = '', ...rest] = text.split('.')
  const decimal = rest.join('').slice(0, 1)
  return (whole.slice(0, 4) + (text.includes('.') ? `.${decimal}` : '')).slice(0, 6)
}

export function formatMl(ml) {
  const value = Number(ml)
  if (!Number.isFinite(value)) return ''
  return `${Math.round(value * 10) / 10} ml`
}

export function clampOnHand(value) {
  const count = Math.round(Number(value))
  if (!Number.isFinite(count) || count < 1) return 1
  return Math.min(MAX_ON_HAND, count)
}

export function totalMl(sizeMl, count) {
  const size = Number(sizeMl)
  if (!(size > 0)) return null
  return Math.round(size * clampOnHand(count) * 10) / 10
}

// "Sample · 1.5 ml ×3" — the label a card chip and the bottle picker show.
export function formatSizeLabel({ format, ml, count = 1 }) {
  const parts = [FORMAT_LABELS[format] || null, ml != null ? formatMl(ml) : null].filter(Boolean)
  const label = parts.join(' · ')
  return count > 1 ? `${label} ×${count}` : label
}

// Card size entries for one collection row. On-hand bottles that carry a kind are grouped
// by kind + size; bottles without a kind keep the legacy behaviour (fragrances.bottle_ml).
// Returns [] when the caller should fall back to the legacy entry.
export function formatSizeEntries(fragrance, inventory = []) {
  const onHand = inventory.filter(bottle => ON_HAND_STATUSES.has(bottle.status))
  const withFormat = onHand.filter(bottle => bottle.bottle_format)
  if (withFormat.length === 0) return []
  const groups = new Map()
  for (const bottle of withFormat) {
    const ml = bottle.size_ml == null ? null : Number(bottle.size_ml)
    const key = `${bottle.bottle_format}\u0000${ml}`
    const group = groups.get(key) || { format: bottle.bottle_format, ml, count: 0 }
    group.count += 1
    groups.set(key, group)
  }
  const entries = [...groups.values()].map((group, index) => ({
    id: fragrance.id,
    key: `${fragrance.id}-${group.format}-${group.ml}-${index}`,
    ml: group.ml,
    count: group.count,
    format: group.format,
    concentration: fragrance.concentration || '',
    isSample: group.format === 'sample',
    finished_at: fragrance.finished_at || null,
  }))
  // A full bottle of the same fragrance whose kind was never stated still shows.
  const unstated = onHand.filter(bottle => !bottle.bottle_format)
  if (unstated.length > 0) {
    const ml = unstated[0].size_ml ?? fragrance.bottle_ml
    if (ml != null) {
      entries.push({
        id: fragrance.id,
        key: `${fragrance.id}-unstated`,
        ml: Number(ml),
        count: 1,
        format: null,
        concentration: fragrance.concentration || '',
        isSample: false,
        finished_at: fragrance.finished_at || null,
      })
    }
  }
  return entries
}

export function indexInventory(rows = []) {
  const byFragrance = new Map()
  for (const row of rows) {
    if (!row?.fragrance_id) continue
    const list = byFragrance.get(row.fragrance_id) || []
    list.push(row)
    byFragrance.set(row.fragrance_id, list)
  }
  return byFragrance
}

// The answer the after-add question starts from: the bottle's kind, exact size and how many
// of that kind are on hand. Prefers bottles that already carry a kind.
export function bottleDetailsFromInventory(inventory = [], fallbackMl = null) {
  const onHand = inventory
    .filter(bottle => ON_HAND_STATUSES.has(bottle.status))
    .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')))
  const first = onHand.find(bottle => bottle.bottle_format) || onHand[0] || null
  const format = first?.bottle_format || null
  const sizeMl = first?.size_ml != null ? Number(first.size_ml) : (fallbackMl != null ? Number(fallbackMl) : null)
  const onHandCount = format
    ? onHand.filter(bottle => bottle.bottle_format === format && Number(bottle.size_ml) === sizeMl).length
    : 1
  return { bottle_format: format, size_ml: sizeMl, on_hand: Math.max(1, onHandCount), bottle_id: first?.id || null }
}

// A just-added bottle of 3 ml or less is almost always a sample; suggest it (the person
// can change it). Travel cannot be told from size alone, so it is never guessed.
export function suggestedFormat(details) {
  if (details?.bottle_format) return details.bottle_format
  const ml = Number(details?.size_ml)
  return ml > 0 && ml <= 3 ? 'sample' : null
}

// What gets sent to set_bottle_format_v1, or null when the person chose no kind.
export function bottleFormatPayload(values) {
  const format = values?.bottle_format || null
  if (!format) return null
  if (!FORMAT_LABELS[format]) throw new Error('Choose Full bottle, Travel or Sample.')
  // Name the exact bottle the question was about, so a second group of the same kind is
  // never relabelled or counted by mistake.
  const p_bottle_id = values.bottle_id || null
  if (!isCountedFormat(format)) return { p_format: format, p_size_ml: null, p_on_hand: null, p_bottle_id }
  return {
    p_format: format,
    p_size_ml: parseBottleSize(values.size_ml),
    p_on_hand: clampOnHand(values.on_hand ?? 1),
    p_bottle_id,
  }
}

export async function loadBottleDetails(client, fragranceId, fallbackMl = null) {
  const { data, error } = await client
    .from('bottle_inventory')
    .select('id,fragrance_id,size_ml,status,bottle_format,created_at')
    .eq('fragrance_id', fragranceId)
  if (error) throw error
  return bottleDetailsFromInventory(data || [], fallbackMl)
}

export async function saveBottleFormat(client, fragranceId, values) {
  const payload = bottleFormatPayload(values)
  if (!payload) return null
  const { data, error } = await client.rpc('set_bottle_format_v1', { p_fragrance_id: fragranceId, ...payload })
  if (error) throw error
  return data
}

export function bottleFormatSummary(values) {
  const format = values?.bottle_format
  if (!format) return null
  if (!isCountedFormat(format)) return FORMAT_LABELS[format]
  const count = clampOnHand(values.on_hand ?? 1)
  const label = formatSizeLabel({ format, ml: values.size_ml, count })
  const total = count > 1 ? totalMl(values.size_ml, count) : null
  return total ? `${label} (${formatMl(total)} total)` : label
}

// ── The after-add question and the log page save origin and kind together ────────────────

export async function loadBottleAnswers(client, fragranceId, fallbackMl = null) {
  const [origin, details] = await Promise.all([
    loadOrigin(client, fragranceId),
    loadBottleDetails(client, fragranceId, fallbackMl).catch(() => ({ bottle_format: null, size_ml: fallbackMl, on_hand: 1, bottle_id: null })),
  ])
  return { ...origin, ...details }
}

export async function saveBottleAnswers(client, fragranceId, values) {
  const origin = await saveOrigin(client, fragranceId, values)
  const formatResult = await saveBottleFormat(client, fragranceId, values)
  if (formatResult) publishBottlesChanged(fragranceId)
  return {
    ...origin,
    bottle_format: formatResult?.bottle_format ?? values.bottle_format ?? null,
    size_ml: formatResult?.size_ml ?? values.size_ml ?? null,
    on_hand: formatResult?.on_hand ?? values.on_hand ?? 1,
    bottle_id: formatResult?.bottle_id ?? values.bottle_id ?? null,
  }
}
